/**
 * Emote bubbles. Content names an emote freely (`showEmote('laugh')`, schedule `{ kind: 'emote', emote: 'coin' }`,
 * dialogue `emote: { actor, emote: 'sweat' }`, timeline `{ op: 'emote', emote: '?' }`); the art is the UI emote
 * atlas `ui-emote-bubbles` (16 × 16 frames named by icon, no pivot, manifest group `shared` — tools/assets/README.md
 * "Runtime key convention").
 *
 * Resolution (`resolveEmote`): atlas frame `<name>` → atlas frame `EMOTE_ALIAS[name]` → a standalone texture
 * `emote-<name>` (a scene's own placeholder) → null, and `Actor` then draws the DEV bubble `dev-emote-<name>`.
 * `EMOTE_ALIAS` maps every name the chapters request onto the eight icons that exist until the UI art stage ships
 * more (docs/asset-requests/characters-builder.md); once the atlas carries a frame under the content name, that
 * frame wins and the alias is bypassed without a code change. tests/unit/characters.emotes.test.ts scans the
 * content for emote names and fails when one is neither an icon nor aliased.
 */
export const EMOTE_ATLAS = 'ui-emote-bubbles';

/** Frames the atlas ships today (tools/assets/processed.json `ui-emote-bubbles` meta.states, in atlas order). */
export const EMOTE_ICONS = ['exclaim', 'question', 'sweat', 'heart', 'dots', 'anger', 'note', 'bulb'] as const;
export type EmoteIcon = (typeof EMOTE_ICONS)[number];

/** Content emote name → icon on the atlas (names that are icons themselves resolve directly and are not listed). */
export const EMOTE_ALIAS: Readonly<Record<string, EmoteIcon>> = {
  // ! — calls, greetings, sales pitches (蜡烛！一金币一截！), wares held up, sudden notice
  '!': 'exclaim', shout: 'exclaim', wave: 'exclaim', coin: 'exclaim', egg: 'exclaim', cactus: 'exclaim',
  alert: 'exclaim', surprise: 'exclaim', surprised: 'exclaim', hail: 'exclaim', call: 'exclaim',
  // ? — doubt, shrugs, looking around
  '?': 'question', shrug: 'question', 'look-up': 'question', 'look-sky': 'question', puzzled: 'question', confused: 'question', wonder: 'question',
  // drop — effort at the crane / ropes / hull, hurrying from the rain, nerves, a flushed face, tears
  heave: 'sweat', pull: 'sweat', hammer: 'sweat', rope: 'sweat', hurry: 'sweat', unease: 'sweat', flushed: 'sweat',
  tired: 'sweat', cry: 'sweat', nervous: 'sweat', panic: 'sweat', embarrassed: 'sweat',
  // heart — warmth and delight (老板 welcoming guests, a drinker relishing the cup)
  happy: 'heart', love: 'heart', drink: 'heart', pleased: 'heart', delighted: 'heart',
  // … — silence, brooding, sadness, hushing, a dimmed face
  '...': 'dots', '…': 'dots', think: 'dots', dim: 'dots', sad: 'dots', sigh: 'dots', shh: 'dots', hush: 'dots', sniff: 'dots',
  frown: 'dots', shake: 'dots', 'eyes-closed': 'dots', sleep: 'dots', ponder: 'dots', silence: 'dots',
  // anger mark
  angry: 'anger', rage: 'anger', mad: 'anger', scorn: 'anger',
  // ♪ — laughter, giggles, humming at work (folding cloth, fanning), a smug whistle, chants
  laugh: 'note', giggle: 'note', fan: 'note', fold: 'note', smug: 'note', chant: 'note', sing: 'note', hum: 'note', whistle: 'note',
  // bulb — an idea, the trader's darting eyes
  blink: 'bulb', idea: 'bulb', aha: 'bulb', realise: 'bulb', realize: 'bulb', notice: 'bulb', remember: 'bulb',
};

/** Icon a content name lands on (itself when it is an icon), or null when the name is unknown. */
export function emoteIconFor(name: string): EmoteIcon | null {
  if ((EMOTE_ICONS as readonly string[]).includes(name)) return name as EmoteIcon;
  return EMOTE_ALIAS[name] ?? null;
}

/** Texture queries (structural, so this file stays Phaser-free for Node tests). */
export interface EmoteLookup { hasFrame(texture: string, frame: string): boolean; hasTexture(key: string): boolean }
/** Where an emote draws from: `frame` is null for a standalone image. */
export interface EmoteRef { texture: string; frame: string | null; via: 'atlas' | 'alias' | 'image' }

/** Atlas frame under the content name → aliased icon frame → `emote-<name>` texture → null (dev bubble). */
export function resolveEmote(name: string, look: EmoteLookup): EmoteRef | null {
  if (!name || name.startsWith('__')) return null;
  if (look.hasFrame(EMOTE_ATLAS, name)) return { texture: EMOTE_ATLAS, frame: name, via: 'atlas' };
  const alias = EMOTE_ALIAS[name];
  if (alias && look.hasFrame(EMOTE_ATLAS, alias)) return { texture: EMOTE_ATLAS, frame: alias, via: 'alias' };
  if (look.hasTexture(`emote-${name}`)) return { texture: `emote-${name}`, frame: null, via: 'image' };
  return null;
}
